import { Box, Tooltip, Typography } from '@mui/material'
import React from 'react'

// Build info injected by Vite at compile time
const BUILD_VERSION = typeof __BUILD_VERSION__ !== 'undefined' ? __BUILD_VERSION__ : 'dev'
const BUILD_TIMESTAMP =
  typeof __BUILD_TIMESTAMP__ !== 'undefined' ? __BUILD_TIMESTAMP__ : new Date().toISOString()
const BUILD_TAG = typeof __BUILD_TAG__ !== 'undefined' ? __BUILD_TAG__ : 'dev'
const BUILD_GIT_SHA = typeof __BUILD_GIT_SHA__ !== 'undefined' ? __BUILD_GIT_SHA__ : 'unknown'
const BUILD_GIT_DIRTY = typeof __BUILD_GIT_DIRTY__ !== 'undefined' ? __BUILD_GIT_DIRTY__ : 'unknown'
const BUILD_GIT_BRANCH =
  typeof __BUILD_GIT_BRANCH__ !== 'undefined' ? __BUILD_GIT_BRANCH__ : 'unknown'

/**
 * A subtle version indicator that stays out of the way.
 * - Nearly invisible until hovered
 * - Shows version, git SHA, and build timestamp on hover
 * - Logs build info to console on mount (operators can paste in bug reports)
 * - Highlights DIRTY builds in red as a smell-test for stale deploys
 * - Fixed position in bottom-right corner
 */
export const VersionBadge: React.FC = () => {
  const showTag = BUILD_TAG !== 'dev' && BUILD_TAG !== 'none'
  const showSha = BUILD_GIT_SHA !== 'unknown' && BUILD_GIT_SHA !== 'dev'
  const isDirty = BUILD_GIT_DIRTY === 'DIRTY'

  // Compact label: "v5.0.0 REL-123 · 4dd9c91" (or "· 4dd9c91·DIRTY" if dirty)
  const versionPart = showTag ? `v${BUILD_VERSION} ${BUILD_TAG}` : `v${BUILD_VERSION}`
  const shaPart = showSha ? `${BUILD_GIT_SHA}${isDirty ? '·DIRTY' : ''}` : ''
  const badgeLabel = shaPart ? `${versionPart} · ${shaPart}` : versionPart

  // Log build info to console once on mount — Henry/Quintin can paste this when
  // filing bugs. console.info (NOT console.log): the prod build strips
  // log/debug (vite.config esbuild.pure); info is the channel that survives
  // for intentional support breadcrumbs like this one.
  React.useEffect(() => {
    const dirtyWarn = isDirty ? ' ⚠️  DIRTY BUILD' : ''
    console.info(
      `%c[VetPo] ${versionPart} · ${BUILD_GIT_SHA} (${BUILD_GIT_BRANCH})${dirtyWarn} | Built: ${BUILD_TIMESTAMP}`,
      isDirty
        ? 'color: #c62828; font-size: 11px; font-weight: bold;'
        : 'color: #888; font-size: 10px;',
    )
  }, [versionPart, isDirty])

  // Format timestamp for display
  const formattedTime = React.useMemo(() => {
    try {
      const date = new Date(BUILD_TIMESTAMP)
      return date.toLocaleString()
    } catch {
      return BUILD_TIMESTAMP
    }
  }, [])

  const tooltipContent = (
    <Box sx={{ p: 0.5 }}>
      <Typography variant="caption" component="div">
        Version: {BUILD_VERSION}
      </Typography>
      {showTag && (
        <Typography variant="caption" component="div">
          Tag: {BUILD_TAG}
        </Typography>
      )}
      {showSha && (
        <Typography variant="caption" component="div">
          SHA: {BUILD_GIT_SHA} ({BUILD_GIT_BRANCH})
        </Typography>
      )}
      {isDirty && (
        <Typography variant="caption" component="div" sx={{ color: '#ff8a80', fontWeight: 'bold' }}>
          ⚠ Built from DIRTY tree — uncommitted changes baked in
        </Typography>
      )}
      <Typography variant="caption" component="div" sx={{ color: 'grey.400' }}>
        Built: {formattedTime}
      </Typography>
    </Box>
  )

  return (
    <Tooltip title={tooltipContent} placement="top-start" arrow>
      <Box
        sx={{
          position: 'fixed',
          bottom: 4,
          right: 4,
          zIndex: 9999,
          cursor: 'help',
          // Nearly invisible until hovered. DIRTY builds get a faint red tint
          // even at rest so reviewers can spot them without hovering.
          opacity: isDirty ? 0.4 : 0.15,
          transition: 'opacity 0.2s ease',
          '&:hover': {
            opacity: 1,
          },
          // Tiny indicator
          fontSize: '9px',
          fontFamily: 'monospace',
          color: isDirty ? '#c62828' : 'text.secondary',
          backgroundColor: 'background.paper',
          borderRadius: '2px',
          px: 0.5,
          userSelect: 'none',
        }}
      >
        {badgeLabel}
      </Box>
    </Tooltip>
  )
}

export default VersionBadge
